import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Brain } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'

interface ContentFThinkingBlockProps {
  thinking: string
  isStreaming?: boolean
  isProcessing?: boolean  // Node is still working (even if thinking text stopped)
  label?: string  // Optional label (e.g., "Evaluator", "Editor", "Responder")
  color?: string  // Optional color class (e.g., "text-amber-600")
}

export function ContentFThinkingBlock({ thinking, isStreaming = false, isProcessing = false, label = 'Thinking', color }: ContentFThinkingBlockProps) {
  const [isExpanded, setIsExpanded] = useState(isStreaming)

  // Auto-expand when streaming starts
  useEffect(() => {
    if (isStreaming) {
      setIsExpanded(true)
    }
  }, [isStreaming])

  if (!thinking) return null

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.08em]',
          'hover:text-foreground transition-colors',
          color || (isStreaming ? 'text-foreground/70' : 'text-muted-foreground')
        )}
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
        )}
        <Brain className={cn("h-3 w-3", (isStreaming || isProcessing) && "animate-pulse")} strokeWidth={1.5} />
        <span>{label}</span>
        {isStreaming && (
          <span className="inline-block w-1.5 h-3 bg-foreground/50 animate-pulse ml-1" />
        )}
        {isProcessing && !isStreaming && (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse ml-1.5" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 pl-3 border-l border-border/40 overflow-hidden">
          <div className={cn(
            // Base typography - scaled down, muted
            "text-[11px] leading-normal text-muted-foreground/80",
            "break-words",
            // Paragraphs - compact
            "[&>p]:mb-2 [&>p]:mt-0",
            // Headings - compact
            "[&>h1]:text-xs [&>h1]:font-medium [&>h1]:text-muted-foreground [&>h1]:mb-1 [&>h1]:mt-2",
            "[&>h2]:text-[11px] [&>h2]:font-medium [&>h2]:text-muted-foreground [&>h2]:mb-1 [&>h2]:mt-2",
            "[&>h3]:text-[11px] [&>h3]:font-medium [&>h3]:text-muted-foreground [&>h3]:mb-1 [&>h3]:mt-2",
            // Lists - base indent with markers inside
            "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:ml-2 [&_ul]:my-2",
            "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:ml-2 [&_ol]:my-2",
            "[&_li]:mb-1 [&_li]:leading-normal [&_li]:pl-1",
            // Nested lists - additional indent and different bullet style
            "[&_ul_ul]:pl-4 [&_ul_ul]:ml-2 [&_ul_ul]:my-1 [&_ul_ul]:list-[circle]",
            "[&_ol_ol]:pl-4 [&_ol_ol]:ml-2 [&_ol_ol]:my-1",
            "[&_ul_ul_ul]:list-[square]",
            // Emphasis
            "[&_strong]:font-medium [&_strong]:text-muted-foreground",
            "[&_em]:italic",
            // Code - compact
            "[&_code]:text-[10px] [&_code]:bg-muted/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:break-words",
            // Code blocks - compact
            "[&_pre]:overflow-x-auto [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:bg-muted/30 [&_pre]:p-1.5 [&_pre]:rounded [&_pre]:my-2 [&_pre]:text-[10px]",
            // Blockquotes - compact
            "[&_blockquote]:border-l-2 [&_blockquote]:border-border/50 [&_blockquote]:pl-2 [&_blockquote]:italic [&_blockquote]:my-2"
          )}>
            <ReactMarkdown>{thinking}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}
